import { GatsbyImage } from "gatsby-plugin-image";
import styled from "styled-components";
import { Link as GatsbyLink } from "gatsby";
import { TextLinkCss, h6 } from "@/styles/typography";

const Header = styled.header<{ $scrolled: boolean }>`
  position: sticky;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 1000;
  background-color: ${({ theme }) => theme.colors.coreLight};
`;

// - - - - - LOGO - - - - -
const LogoWrapper = styled(GatsbyLink)`
  padding: 8px 16px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    padding: 0 24px;
  }
`;

const Logo = styled(GatsbyImage)`
  img {
    height: auto;
  }
`;

// - - - - - NAVBAR - - - - -
const NavBar = styled.nav`
  height: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  height: 56px;
  background-color: ${({ theme }) => theme.colors.coreLight};
  z-index: 1000;

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    height: 64px;
  }
`;

const NavWrapperDesktop = styled.ul`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 100%;
  }
`;

// LINKS
const LinkWrapper = styled.li<{
  $bgColor?: string;
  $subIndex: boolean;
}>`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  @media screen and (max-width: ${({ theme }) => theme.breakpoints.xxl}) {
    ${({ $subIndex, $bgColor }) =>
      $subIndex && `background-color: ${$bgColor};`}
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    background-color: transparent;
    transition: all 0.2s ease-in-out;

    &:hover {
      background-color: ${({ $bgColor }) => $bgColor};
    }
  }
`;

const Link = styled(GatsbyLink)<{
  $subIndex: boolean;
  $subMenuOpen: boolean;
}>`
  font-family: "Frutiger Bold";
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: 120%;
  letter-spacing: 0.27px;
  color: ${({ theme }) => theme.colors.textHeading};
  position: relative;
  display: inline-block;
  white-space: nowrap;
  padding: 16px 0 16px 16px;
  width: 100%;
  text-decoration: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    padding: 26px 0px 23px 24px;
    font-size: 16px;

    &.active {
      &::after {
        content: "";
        position: absolute;
        bottom: 23px;
        left: 25px;
        width: calc(100% - 25px);
        height: 2px;
        background-color: ${({ theme }) => theme.colors.textHeading};
        transition: width 0.3s ease;
      }
    }
  }
`;

// SUBMENU
const SubMenuOverlay = styled.div<{
  $subIndex: boolean;
  $bgColor: string;
  $cta: string;
}>`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    width: 100%;
    height: 309px;
    overflow: hidden;
    z-index: 100;

    ${({ $subIndex, $bgColor, $cta }) =>
      $subIndex &&
      `
      display: flex;
      background-color: ${$bgColor};
      position: absolute;
      top: ${$cta === "yes" ? "calc(64px + 38px)" : "64px"};
      left: 0;
    `}
  }
`;

const SubLinksContainer = styled.nav`
  &.bg-color {
    background-color: ${({ theme }) => theme.colors.lightPink};
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: flex;
    flex-direction: row;
    padding: 40px 48px;
    gap: 40px;

    &.first-container {
      width: 45%;
    }

    &.bg-color {
      width: 30%;
    }
  }
`;

const Backdrop = styled.div`
  display: none;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: flex;
    position: fixed;
    z-index: 2;
    top: 150px;
    left: 0;
    width: 100%;
    height: 100vh;
    transition: all 0.3s ease-in-out;
    transition-delay: 0.3s;
  }
`;

const ArrowWrapper = styled.button<{
  $subIndex: boolean;
}>`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 16px 16px 16px 20px;
  cursor: pointer;
  background: transparent;
  border: none;
  height: 100%;

  svg {
    width: 16px;
    height: 16px;
  }

  ${({ $subIndex, theme }) =>
    $subIndex &&
    `
    svg {
      @media screen and (max-width: ${theme.breakpoints.xxl}) {
      transform: rotate(-180deg);
      }
    }
  `}

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    padding: 32px 24px 32px 8px;
  }
`;

// SUBLINKS
const SubLinksWrapper = styled.ul`
  display: flex;
  flex-direction: column;

  a {
    text-decoration: underline;
    text-decoration-color: transparent;
    color: ${({ theme }) => theme.colors.textHeading};
    transition: all 0.2s ease-in-out;

    &:hover {
      text-decoration-color: ${({ theme }) => theme.colors.textHeading};
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    gap: 16px;
  }
`;

const SubLinks = styled.ul<{ $showSubSub?: boolean }>`
  list-style: none;
  font-family: "Frutiger Light";
  font-size: 16px;
  font-style: normal;
  font-weight: 300;
  line-height: 120%;
  display: ${({ $showSubSub }) => ($showSubSub ? "flex" : "none")};
  padding: 0 24px 16px 24px;
  flex-direction: column;
  gap: 24px;

  a {
    text-decoration: underline;
    text-decoration-color: transparent;
    transition: all 0.2s ease-in-out;
    color: ${({ theme }) => theme.colors.textHeading};

    &:hover {
      text-decoration-color: ${({ theme }) => theme.colors.textHeading};
    }
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: flex;
    padding: 0;
    gap: 16px;
  }
`;

const SubLinkTitleWrapper = styled.li`
  display: flex;
  justify-content: space-between;
  align-items: center;

  ${ArrowWrapper} {
    padding: 16px 16px 16px 30px;
  }

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    ${ArrowWrapper} {
      display: none;
    }
  }
`;

const SubLinkTitle = styled(GatsbyLink)`
  ${TextLinkCss}
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 24px;
  width: 100%;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    padding: 0;
  }
`;

const SubLink = styled(GatsbyLink)`
  padding: 0;
  font-weight: 300;
`;

// - - - - MOBILE MENU - - - -
const MenuBtn = styled.button<{ $isOpen: boolean }>`
  background-color: ${({ theme }) => theme.colors.primaryPurple};
  height: 100%;
  width: 96px;
  border: none;
  cursor: pointer;
  align-items: center;
  color: ${({ theme }) => theme.colors.coreLight};

  svg {
    path {
      stroke: ${({ theme }) => theme.colors.coreLight};
    }
    margin-left: 8px;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: none;
  }
`;

const MobileMenu = styled.div`
  position: fixed;
  top: 56px;
  left: 0;
  width: 100%;
  z-index: 100;
  overflow: auto;
  height: 100vh;

  @media screen and (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: none;
  }
`;

const NavWrapperMobile = styled.ul`
  background-color: ${({ theme }) => theme.colors.coreMedium};
`;

const MobileSubMenuOverlay = styled.nav<{
  $subIndex: boolean;
  $bgColor: string;
  $cta: string;
}>`
  display: none;
  width: 100%;
  overflow: hidden;

  ${({ $subIndex, $bgColor, theme, $cta }) =>
    $subIndex &&
    `
      display: block;
      background-color: ${$bgColor};

      @media screen and (min-width: ${theme.breakpoints.xxl}) {
        display: none;
      }
    `}
`;

// - - - - CTA BLOCK - - - -
const CTABlockMobile = styled.div`
  background-color: ${({ theme }) => theme.colors.darkPurple};

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: none;
  }
`;

const CTABlockDesktop = styled.div`
  display: none;

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    display: flex;
    width: 100%;
    height: 38px;
    z-index: 1000;
    background-color: ${({ theme }) => theme.colors.primaryPurple};
  }
`;

const CTALinksWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: start;
  padding: 32px 16px;
  gap: 24px;

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    align-items: center;
    flex-direction: row;
    justify-content: flex-end;
    height: 100%;
    width: 100%;
    padding: 0 24px;
    gap: 32px;
  }

  &.arrow-no-transform {
    svg {
      transform: rotate(0deg);
    }
  }
`;

// CTA IN SUBMENU
const CTASubMenu = styled.div`
  background-color: ${({ theme }) => theme.colors.primaryPurple};
  display: flex;
  flex-direction: column;
  padding: 40px 16px;
  gap: 24px;
  height: 100%;

  a {
    align-self: flex-start;
    height: fit-content;
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    max-width: 25%;
    padding: 32px;
    justify-content: space-between;
  }
`;

const CTAText = styled(h6)`
  color: ${({ theme }) => theme.colors.coreLight};
  width: 60%;

  @media (min-width: ${({ theme }) => theme.breakpoints.xxl}) {
    width: 100%;
  }
`;

// - - - - - SEARCH - - - - -
const SearchBtn = styled.li`
  background-color: ${({ theme }) => theme.colors.coreDark};
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const S = {
  Header,
  LogoWrapper,
  Logo,
  NavBar,
  MenuBtn,
  MobileMenu,
  NavWrapperMobile,
  NavWrapperDesktop,
  SubMenuOverlay,
  SubLinkTitleWrapper,
  SubLinksContainer,
  CTABlockMobile,
  CTALinksWrapper,
  CTABlockDesktop,
  CTASubMenu,
  CTAText,
  LinkWrapper,
  Link,
  MobileSubMenuOverlay,
  SubLinks,
  SubLinksWrapper,
  SubLink,
  SubLinkTitle,
  ArrowWrapper,
  SearchBtn,
  Backdrop,
};

export default S;
